import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import Select from "common/src/components/widget/Select";
import KPIChartSortHeader from "./KPIChartSortHeader";
import DedicatedBarChartWidget from "./DedicatedBarChartWidget";
import ToggleSection, {useToggleSection} from "../../../widget/ToggleSection";
import ToggleTagGroupPanel from "./ToggleTagGroupPanel";
import {
  excludeSelectedSiteDirectionsActive,
  includeSelectedSiteDirectionsActive,
  setExcludedSiteDirectionsActive,
  setIncludedSiteDirectionsActive,
  toggleExcludedSiteDirectionActive,
} from "../../../../state/facade/dataFacade";
import {ChartPlotColors} from "../constants/chartColors";
import {selectDataMode} from "../../../../state/slice/dataModeSlice";
import {selectSitesAgg} from "../../../../state/facade/statisticsFacade";
import {formatSLONumber, formatSLOPercent} from "../utils/FormatNumbers";

/* ---------------- helpers for order ---------------- */
const splitSiteDir = (key) => {
  const [site, dir] = String(key ?? "").split(" | ").map((s) => s?.trim());
  return { site, dir };
};

const buildSiteOrderMap = (xAxis, mode) => {
  const map = new Map();
  xAxis.forEach((label, idx) => {
    if (mode === "site") {
      if (!map.has(label)) map.set(label, idx);
    } else if (mode === "site-direction") {
      const { site } = splitSiteDir(label);
      if (site && !map.has(site)) map.set(site, idx);
    }
  });
  return map;
};

const byOrderThenAlpha =
    (orderMap, keySelector = (it) => it?.name) =>
        (a, b) => {
          const ka = keySelector(a) ?? "";
          const kb = keySelector(b) ?? "";
          const ia = orderMap.has(ka) ? orderMap.get(ka) : Number.POSITIVE_INFINITY;
          const ib = orderMap.has(kb) ? orderMap.get(kb) : Number.POSITIVE_INFINITY;
          if (ia !== ib) return ia - ib;
          return ka.localeCompare(kb);
        };

/* ---------------- component ---------------- */
const OverloadsBy = ({ fixedGroupMode, fixedValueMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const dataSourceMode = useSelector(selectDataMode);

  const { data: statsPayload = {}, loading } = useSelector(selectSitesAgg);
  // const { locations = {} } = useSelector((state) => state.siteDataAPI);
  const { locations = {} } = useSelector((s) =>
      (dataSourceMode === "processed"
          ? s.processedSiteDataAPI
          : s.siteDataAPI)
  );

  const excludedSiteDirectionNames = useSelector((s) =>
      (dataSourceMode === "processed"
          ? s.processedMapFiltersSlice.excluded.siteDirections
          : s.mapFiltersSlice.excluded.siteDirections) || []
  );
  const excludedDirections = useSelector((s) =>
      (dataSourceMode === "processed"
          ? s.processedMapFiltersSlice.excluded.directions
          : s.mapFiltersSlice.excluded.directions) || []
  );
  // --- view state ---
  const [mode, setMode] = useState("site");
  const [valueMode, setValueMode] = useState("total");
  const [sortBy, setSortBy] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  useEffect(() => {
    if (fixedGroupMode) setMode(fixedGroupMode);
  }, [fixedGroupMode]);
  useEffect(() => {
    if (fixedValueMode) setValueMode(fixedValueMode);
  }, [fixedValueMode]);

  const agg = useMemo(() => {
    return dailyAggregationAverageByKey(
        statsPayload,
        mode,
        sortBy,
        sortDirection,
        valueMode,
        [], // no full site exclusion
        excludedDirections,
        excludedSiteDirectionNames
    );
  }, [
    statsPayload,
    mode,
    sortBy,
    sortDirection,
    valueMode,
    excludedDirections,
    excludedSiteDirectionNames,
  ]);

  const { xAxis, series, colors } = agg;

  // --- build order maps from sorted xAxis
  const siteOrderMap = useMemo(
      () => buildSiteOrderMap(xAxis, mode),
      [xAxis, mode]
  );
  const keySelectorForMode = (mode) => (it) => {
    if (!it?.name) return "";
    if (mode === "site") {
      return splitSiteDir(it.name).site;       // only site part
    }
    if (mode === "direction") {
      return splitSiteDir(it.name).dir;        // only direction part
    }
    return it.name; // site-direction stays as-is
  };
  // --- sort toggle items consistently
  const allSiteDirectionItems = useMemo(() => {
    const set = new Set();
    Object.values(statsPayload || {}).forEach((bundle) => {
      (bundle?.dailyStats || []).forEach((row) => {
        if (row?.siteDirectionName) set.add(String(row.siteDirectionName));
      });
    });
    return Array.from(set)
    .map((name) => ({ name }))
    .sort(  byOrderThenAlpha(siteOrderMap, keySelectorForMode(mode))
    );
  }, [statsPayload, siteOrderMap]);

  const excludedSiteDirectionItems = useMemo(() => {
    return excludedSiteDirectionNames
    .map((name) => ({ name }))
    .sort(  byOrderThenAlpha(siteOrderMap, keySelectorForMode(mode))
    );
  }, [excludedSiteDirectionNames, siteOrderMap]);

  // --- sortable fields based on valueMode
  const sortableFields = useMemo(() => {
    if (valueMode === "ratio") {
      return [
        { key: "name", labelKey: "dashboards_view.kpi3.site" },
        {
          key: "%legal",
          labelKey:
              "dashboards_view.kpi1.bar_overload_by_site.series.legally_loaded_rate",
        },
        {
          key: "%only_axle",
          labelKey:
              "dashboards_view.kpi1.bar_overload_by_site.series.only_axle",
        },
        {
          key: "%only_mass",
          labelKey:
              "dashboards_view.kpi1.bar_overload_by_site.series.only_mass",
        },
        {
          key: "%both",
          labelKey: "dashboards_view.kpi1.bar_overload_by_site.series.both",
        },
      ];
    }
    if (valueMode === "dailyAvg") {
      return [
        { key: "name", labelKey: "dashboards_view.kpi3.site" },
        { key: "avg_total", labelKey: "dashboards_view.kpi3.vehicle_count" },
        {
          key: "avg_legal",
          labelKey:
              "dashboards_view.kpi1.bar_overload_by_site.series.legally_loaded",
        },
        {
          key: "avg_only_axle",
          labelKey:
              "dashboards_view.kpi1.bar_overload_by_site.series.only_axle",
        },
        {
          key: "avg_only_mass",
          labelKey:
              "dashboards_view.kpi1.bar_overload_by_site.series.only_mass",
        },
        {
          key: "avg_both",
          labelKey: "dashboards_view.kpi1.bar_overload_by_site.series.both",
        },
      ];
    }
    return [
      { key: "name", labelKey: "dashboards_view.kpi3.site" },
      { key: "total", labelKey: "dashboards_view.kpi3.vehicle_count" },
      {
        key: "sum_legal",
        labelKey:
            "dashboards_view.kpi1.bar_overload_by_site.series.legally_loaded",
      },
      {
        key: "sum_only_axle",
        labelKey: "dashboards_view.kpi1.bar_overload_by_site.series.only_axle",
      },
      {
        key: "sum_only_mass",
        labelKey: "dashboards_view.kpi1.bar_overload_by_site.series.only_mass",
      },
      {
        key: "sum_both",
        labelKey: "dashboards_view.kpi1.bar_overload_by_site.series.both",
      },
    ];
  }, [valueMode]);

  const { isHidden, togglerKey } = useToggleSection(["footer"], ["footer"]);
  const { titleKey, yAxisKey, seriesPrefix } = getOverloadChartTranslationKeys(
      mode
  );

  // --- formatters
  const isPercent = valueMode === "ratio";
  const fmtNumber = (val) => formatSLONumber(val, 0);
  const fmtPercent = (val) => formatSLOPercent(val, 1)

  return (
      <>
        {!fixedGroupMode && (
            <div className="field mb-4" style={{ maxWidth: 300 }}>
              <Select
                  options={[
                    { name: "site", label: t("dashboards.overload_by.site") },
                    {
                      name: "site-direction",
                      label: t("dashboards.overload_by.site_direction"),
                    },
                    { name: "direction", label: t("dashboards.overload_by.direction") },
                  ]}
                  optionsValue="name"
                  optionsLabel="label"
                  label={t("dashboards_view.mode")}
                  selectedItem={mode}
                  onChange={setMode}
              />
            </div>
        )}

        {!fixedValueMode && (
            <div className="field mb-4" style={{ maxWidth: 300 }}>
              <Select
                  options={[
                    { name: "ratio", label: t("dashboards_view.kpi1.modes.ratio") },
                    {
                      name: "dailyAvg",
                      label: t("dashboards_view.kpi1.modes.daily_avg"),
                    },
                    { name: "total", label: t("dashboards_view.kpi1.modes.total") },
                  ]}
                  optionsValue="name"
                  optionsLabel="label"
                  label={t("dashboards_view.mode")}
                  selectedItem={valueMode}
                  onChange={setValueMode}
              />
            </div>
        )}

        <KPIChartSortHeader
            fields={sortableFields}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={(key, direction) => {
              setSortBy(key);
              setSortDirection(direction);
            }}
        />

        <DedicatedBarChartWidget
            stacked
            id="kpi1-bar-overload-by"
            title={t(titleKey)}
            xAxis={xAxis}
            series={series.map((s) => ({
              ...s,
              name: t(`${seriesPrefix}.${s.name.split(".").pop()}`),
            }))}
            colors={colors}
            isLoading={loading}
            dataLabelFunction={(val) =>
                isPercent ? fmtPercent(val) : fmtNumber(val)
            }
            options={{
              xaxis: {
                labels: {
                  formatter: (val) => {
                    if (!val) return val;
                    if (mode === "site-direction") return val;
                    const [site, dir] = String(val).split(" | ");
                    const siteLabel =
                        locations?.[site?.trim()]?.locationName ||
                        site?.trim() ||
                        val;
                    return dir ? `${siteLabel} | ${dir.trim()}` : siteLabel;
                  },
                },
              },
              yaxis: {
                title: { text: valueMode !== "ratio" ? t(yAxisKey) : "" },
                labels: {
                  formatter: (val) =>
                      isPercent ? fmtPercent(val) : fmtNumber(val),
                },
              },
              tooltip: {
                shared: true,
                intersect: false,
                followCursor: true,
                hideEmptySeries: false,
                y: {
                  formatter: (val) =>
                      isPercent ? fmtPercent(val) : fmtNumber(val),
                },
              },
              chart: {
                type: 'bar',
                stacked: true,
                // stackType: '100%',
              zoom: {
                enabled: true,
                type: "x",
                autoScaleYAxis: true,
                allowMouseWheelZoom: false,
              },
              toolbar: {
                enabled: true,

                show: true,
                tools: {
                  download: true,
                  selection: false,
                  zoom: true,
                  zoomIn: true,
                  zoomOut: true,
                  pan: true,
                  reset: true
                },
              },
            }}}
        />

        <ToggleSection
            sectionKey="footer"
            isHidden={isHidden}
            onToggle={togglerKey}
            title={t("dashboards.tabs.PKI3_content.filter")}
        >
          {/*<ToggleTagGroupPanel label={t("dashboards_view.kpi3.site_direction_selection")} {...panelProps} />*/}

          <ToggleTagGroupPanel
              label={t("dashboards_view.kpi3.site_direction_selection")}
              allItems={allSiteDirectionItems}
              excludedItems={excludedSiteDirectionItems}
              id="name"
              labelKeys={["name"]}
              onToggleItem={(item) =>
                  dispatch(toggleExcludedSiteDirectionActive(item.name))
              }
              onIncludeAllSelectedFromExclude={(items) =>
                  dispatch(includeSelectedSiteDirectionsActive(items.map((i) => i.name)))
              }
              onExcludeAllSelectedFromInclude={(items) =>
                  dispatch(excludeSelectedSiteDirectionsActive(items.map((i) => i.name)))
              }
              onFlipSelection={() => {
                const all = allSiteDirectionItems.map((i) => i.name);
                const excluded = new Set(excludedSiteDirectionNames);
                const toInclude = all.filter((n) => excluded.has(n));
                const toExclude = all.filter((n) => !excluded.has(n));
                dispatch(setIncludedSiteDirectionsActive(toExclude));
                dispatch(setExcludedSiteDirectionsActive(toInclude));
              }}
          />
        </ToggleSection>
      </>
  );
};

export default OverloadsBy;

const normDir = (d) => (d && d.trim() !== "" ? d.trim() : "Unknown");

/**
 * Aggregates daily stats into group-level averages / ratios / totals.
 * Filtering always respects siteDirectionName.
 */
export function dailyAggregationAverageByKey(
    payload = {},
    mode = "site",
    sortBy = "name",
    sortDirection = "asc",
    valueMode = "ratio",
    excludedSites = [],
    excludedDirections = [],
    excludedSiteDirectionNames = []
) {
  const clamp = (n, min = 0) =>
      Number.isFinite(+n) ? Math.max(min, +n) : 0;

  const isSiteExcluded = (list, site) =>
      Array.isArray(list) && list.includes(site);
  const isDirExcluded = (list, dir) =>
      Array.isArray(list) && list.includes(normDir(dir));
  const isSDExcluded = (list, sdn) =>
      Array.isArray(list) && list.includes(String(sdn || "").trim());

  const groupMap = {};
  const internalValueMode = valueMode === "total" ? "sum" : valueMode;

  Object.entries(payload || {}).forEach(([locationId, data]) => {
    if (isSiteExcluded(excludedSites, locationId)) return;

    const dailyStats = Array.isArray(data?.dailyStats)
        ? data.dailyStats
        : [];
    dailyStats.forEach((stat) => {
      const direction = normDir(stat?.direction);
      const sdn = String(
          stat?.siteDirectionName || `${locationId} | ${direction}`
      ).trim();

      if (isDirExcluded(excludedDirections, direction)) return;
      if (isSDExcluded(excludedSiteDirectionNames, sdn)) return;

      const key =
          mode === "site"
              ? locationId
              : mode === "site-direction"
                  ? sdn
                  : direction;

      if (!groupMap[key]) {
        groupMap[key] = {
          name: key,
          total: 0,
          legallyLoaded: 0,
          axle: 0,
          mass: 0,
          both: 0,
          daysSet: new Set(),
          perDayTotals: [],
        };
      }

      const g = groupMap[key];
      const dayTotal = clamp(stat?.numAllVehicles ?? 0, 0);

      g.total += dayTotal;
      g.legallyLoaded += clamp(stat?.numNotOverloadedVehicles ?? 0, 0);
      g.axle += clamp(stat?.numAxleLoadOverLimit ?? 0, 0);
      g.mass += clamp(stat?.numTotalMassOverLimit ?? 0, 0);
      g.both += clamp(stat?.numMassAndAxleOverLimit ?? 0, 0);

      if (stat?.measurementDate) g.daysSet.add(stat.measurementDate);
      g.perDayTotals.push(dayTotal);
    });
  });

  Object.values(groupMap).forEach((g) => {
    g.days = g.daysSet?.size || g.perDayTotals.length || 0;

    g.avg_total = g.days ? g.total / g.days : 0;
    g.avg_legal = g.days ? g.legallyLoaded / g.days : 0;
    g.avg_only_axle = g.days ? g.axle / g.days : 0;
    g.avg_only_mass = g.days ? g.mass / g.days : 0;
    g.avg_both = g.days ? g.both / g.days : 0;

    g["%only_axle"] = g.total ? (g.axle ?? 0) / g.total : 0;
    g["%only_mass"] = g.total ? (g.mass ?? 0) / g.total : 0;
    g["%both"] = g.total ? (g.both ?? 0) / g.total : 0;
    g["%legal"] = g.total ? (g.legallyLoaded ?? 0) / g.total : 0;
  });

  const sortFieldMap = {
    name: "name",
    total: "total",
    sum_legal: "legallyLoaded",
    sum_only_axle: "axle",
    sum_only_mass: "mass",
    sum_both: "both",
    avg_total: "avg_total",
    avg_legal: "avg_legal",
    avg_only_axle: "avg_only_axle",
    avg_only_mass: "avg_only_mass",
    avg_both: "avg_both",
    "%legal": "%legal",
    "%only_axle": "%only_axle",
    "%only_mass": "%only_mass",
    "%both": "%both",
  };

  const field = sortFieldMap[sortBy] ?? "name";
  const groupArray = Object.values(groupMap);

  groupArray.sort((a, b) => {
    const va = a[field];
    const vb = b[field];
    if (typeof va === "string" || typeof vb === "string") {
      return sortDirection === "asc"
          ? String(va ?? "").localeCompare(String(vb ?? ""))
          : String(vb ?? "").localeCompare(String(va ?? ""));
    }
    const na = Number.isFinite(va) ? va : 0;
    const nb = Number.isFinite(vb) ? vb : 0;
    return sortDirection === "asc" ? na - nb : nb - na;
  });

  const xAxis = groupArray.map((g) => g.name);

  const series = [];
  const valuePick = (g) => {
    if (internalValueMode === "ratio") {
      return [g["%legal"], g["%only_axle"], g["%only_mass"], g["%both"]];
    }
    if (internalValueMode === "dailyAvg") {
      return [g.avg_legal, g.avg_only_axle, g.avg_only_mass, g.avg_both];
    }
    return [g.legallyLoaded, g.axle, g.mass, g.both];
  };

  const values = xAxis.map((k) => valuePick(groupMap[k]));
  series.push({
    name: "dashboards_view.kpi1.bar_overload_by_site.series.legally_loaded",
    data: values.map((v) => v[0]),
    type: "bar",
    ...(internalValueMode === "ratio" ? { hidden: true } : {}),
  });
  series.push(
      {
        name: "dashboards_view.kpi1.bar_overload_by_site.series.only_axle",
        data: values.map((v) => v[1]),
        type: "bar",
      },
      {
        name: "dashboards_view.kpi1.bar_overload_by_site.series.only_mass",
        data: values.map((v) => v[2]),
        type: "bar",
      },
      {
        name: "dashboards_view.kpi1.bar_overload_by_site.series.both",
        data: values.map((v) => v[3]),
        type: "bar",
      }
  );

  return {
    xAxis,
    series,
    colors: [
      ChartPlotColors.NO_OVERLOAD,
      ChartPlotColors.AXLE_ONLY,
      ChartPlotColors.MASS_ONLY,
      ChartPlotColors.BOTH,
    ],
    raw: Object.fromEntries(groupArray.map((g) => [g.name, g])),
  };
}
/**
 * @param {"site" | "site-direction" | "direction"} mode
 */
export function getOverloadChartTranslationKeys(mode = "site") {
  let base = "dashboards_view.kpi1.bar_overload_by_site";
  if (mode === "direction")      base = "dashboards_view.kpi1.bar_overload_by_direction";
  if (mode === "site-direction") base = "dashboards_view.kpi1.bar_overload_by_site_direction";

  return {
    titleKey:  `${base}.title`,
    xAxisKey:  `${base}.xAxis`,
    yAxisKey:  `${base}.yAxis`,
    seriesPrefix: `${base}.series`,
  };
}

/**
 * @param {"site" | "site-direction" | "direction"} mode
 */
export function getVehicleTotalsChartTranslationKeys(mode = "site") {
  let base = "dashboards_view.kpi1.bar_totals_by_site";
  if (mode === "direction")      base = "dashboards_view.kpi1.bar_totals_by_direction";
  if (mode === "site-direction") base = "dashboards_view.kpi1.bar_totals_by_site_direction";

  return {
    titleKey:  `${base}.title`,
    xAxisKey:  `${base}.xAxis`,
    yAxisKey:  `${base}.yAxis`,
    seriesPrefix: `${base}.series`,
  };
}
